import { parseStoredTimestamp } from "./date-time";

export const MAX_ORDER_ALERT_AUDIO_BYTES = 1_500_000;

export interface OrderAlertPlaybackState {
  enabled: boolean;
  hasAudio: boolean;
  muted: boolean;
  mode: "ONCE" | "REPEAT_UNTIL_CONFIRMED";
  unconfirmedOrders: number;
}

export function shouldRepeatOrderAlert(
  state: OrderAlertPlaybackState,
): boolean {
  return (
    state.enabled &&
    state.hasAudio &&
    !state.muted &&
    state.mode === "REPEAT_UNTIL_CONFIRMED" &&
    state.unconfirmedOrders > 0
  );
}

export function shouldPlayOrderAlertOnce(
  state: OrderAlertPlaybackState,
  previousUnconfirmedOrders?: number,
): boolean {
  return (
    state.enabled &&
    state.hasAudio &&
    !state.muted &&
    state.mode === "ONCE" &&
    state.unconfirmedOrders > 0 &&
    (previousUnconfirmedOrders === undefined ||
      state.unconfirmedOrders > previousUnconfirmedOrders)
  );
}

export function detectOrderAlertAudioType(
  bytes: Uint8Array,
): { mimeType: "audio/mpeg" | "audio/wav" | "audio/ogg"; extension: "mp3" | "wav" | "ogg" } | null {
  const ascii = (start: number, length: number) =>
    String.fromCharCode(...bytes.slice(start, start + length));
  if (
    bytes.length >= 3 &&
    (ascii(0, 3) === "ID3" ||
      (bytes[0] === 0xff && (bytes[1]! & 0xe0) === 0xe0))
  ) {
    return { mimeType: "audio/mpeg", extension: "mp3" };
  }
  if (bytes.length >= 12 && ascii(0, 4) === "RIFF" && ascii(8, 4) === "WAVE") {
    return { mimeType: "audio/wav", extension: "wav" };
  }
  if (bytes.length >= 4 && ascii(0, 4) === "OggS") {
    return { mimeType: "audio/ogg", extension: "ogg" };
  }
  return null;
}

export function formatOrderStageDuration(
  start: string,
  end?: string,
  now = Date.now(),
): string {
  const startTime = parseStoredTimestamp(start);
  const endTime = end ? parseStoredTimestamp(end) : now;
  if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) return "-";
  const minutes = Math.max(0, Math.floor((endTime - startTime) / 60_000));
  if (minutes < 1) return "kurang dari 1 menit";
  if (minutes < 60) return `${minutes} menit`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours < 24) return remainder ? `${hours} jam ${remainder} menit` : `${hours} jam`;
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours ? `${days} hari ${remainingHours} jam` : `${days} hari`;
}
